import React, {useState} from 'react';
import {Table, Tag} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {useTranslation} from 'react-i18next';
import {getChuteTypeName} from '../types/ChuteEnums';

interface FlowItemRecord {
    key: string;
    exceptionCodes?: string[] | null;
    businessType?: number;
    businessTypeName?: string;
    ttBizType?: string | null;
    ttBizTypeName?: string | null;
    carrierCode?: number;
    carrierName?: string;
    printType?: number;
    printTypeName?: string;
    sortingCode?: string;
    preSortingCode?: string | null;
    lastMileSortingCode?: string | null;
    validChuteCount: number;
    physicalChuteCount: number;
    chuteNumbers: string;
    chuteType?: number | null;
    children?: FlowItemRecord[];
}

interface FlowViewProps {
    data: FlowItemRecord[];
    loading?: boolean;
}

const FlowView: React.FC<FlowViewProps> = ({data, loading = false}) => {
    const {t} = useTranslation();
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

    // 分组维度列：节点只携带自己分组层级（及叶子的全部）字段，缺省显示空
    const dimensionCell = (text: string | null | undefined) => text || '';

    const columns: ColumnsType<FlowItemRecord> = [
        {
            title: t('wms.sorterPlan.detail.flow.businessType'),
            dataIndex: 'businessTypeName',
            key: 'businessTypeName',
            width: 150,
            render: (text: string, record) => {
                // 异常、未使用显示对应的类型名称（具体异常类型见「异常类型」列）
                if (record.chuteType === 1) {
                    return t('wms.sorterPlan.chuteType.exception');
                }
                if (record.chuteType === null) {
                    return t('wms.sorterPlan.chuteType.unused');
                }
                return text || '';
            }
        },
        {
            title: t('wms.sorterPlan.detail.columns.exceptionCodes'),
            dataIndex: 'exceptionCodes',
            key: 'exceptionCodes',
            width: 300,
            render: (codes: string[] | null | undefined, record) => {
                if (record.chuteType !== 1 || !codes?.length) {
                    return '';
                }
                return (
                    <div>
                        {codes.map(code => (
                            <Tag key={code} style={{marginBottom: 2}}>{t(`wms.sorterPlan.exceptionCode.${code}`)}</Tag>
                        ))}
                    </div>
                );
            }
        },
        {
            title: t('wms.sorterPlan.detail.columns.ttBusinessType'),
            dataIndex: 'ttBizTypeName',
            key: 'ttBizTypeName',
            width: 100,
            render: dimensionCell
        },
        {
            title: t('wms.sorterPlan.detail.flow.carrier'),
            dataIndex: 'carrierName',
            key: 'carrierName',
            width: 100,
            render: dimensionCell
        },
        {
            title: t('wms.sorterPlan.detail.columns.sortingRule'),
            dataIndex: 'sortingCode',
            key: 'sortingCode',
            width: 150,
            render: dimensionCell
        },
        {
            title: t('wms.sorterPlan.detail.columns.preSortCode'),
            dataIndex: 'preSortingCode',
            key: 'preSortingCode',
            width: 100,
            render: dimensionCell
        },
        {
            title: t('wms.sorterPlan.detail.flow.printType'),
            dataIndex: 'printTypeName',
            key: 'printTypeName',
            width: 90,
            render: dimensionCell
        },
        {
            title: t('wms.sorterPlan.detail.columns.lastMileSortingCode'),
            dataIndex: 'lastMileSortingCode',
            key: 'lastMileSortingCode',
            width: 150,
            render: dimensionCell
        },
        {
            title: t('wms.sorterPlan.detail.flow.validChuteCount'),
            dataIndex: 'validChuteCount',
            key: 'validChuteCount',
            width: 90,
            align: 'center'
        },
        {
            title: t('wms.sorterPlan.detail.flow.physicalChuteCount'),
            dataIndex: 'physicalChuteCount',
            key: 'physicalChuteCount',
            width: 90,
            align: 'center'
        },
        {
            title: t('wms.sorterPlan.detail.flow.chuteNumbers'),
            dataIndex: 'chuteNumbers',
            key: 'chuteNumbers',
            width: 200,
            render: (text: string, record) => {
                // 未使用格口不显示格口号
                if (record.chuteType === null) {
                    return '--';
                }
                return text || '--';
            }
        },
        {
            title: t('wms.sorterPlan.detail.flow.chuteType'),
            dataIndex: 'chuteType',
            key: 'chuteType',
            width: 120,
            align: 'center',
            render: (type: number | null) => {
                // 显示格口类型
                if (type === null) return '--';
                return getChuteTypeName(type, t);
            }
        }
    ];

    return (
        <div className="flow-view">
            <Table
                columns={columns}
                dataSource={data}
                indentSize={8}
                loading={loading}
                pagination={false}
                scroll={{x: 'max-content', y: 'calc(100vh - 260px)'}}
                sticky
                expandable={{
                    expandedRowKeys,
                    onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
                    // 有 children 的行才可以展开
                    rowExpandable: (record) =>
                        !!record.children &&
                        record.children.length > 0
                }}
                size="middle"
            />
        </div>
    );
};

export default FlowView;
